/* Conversations — record, transcribe on the device, get notes back.
   =================================================================

   THE AUDIO NEVER LEAVES THE DEVICE. Chunks go to IndexedDB, Whisper runs in
   a worker here, and the recording is destroyed when the session closes.
   Exactly one Claude call is made per conversation, after Stop and only if the
   coverage gate passes.

   Moved out of app.html unchanged apart from the onReady() wrapper. It opened
   with `if (!wrap) return;`, which running from <head> would trip on every
   load — turning recording off with nothing in the console to say why.

   NOTE FOR WHOEVER DEBUGS THE NEXT iPad CRASH: this file moved out of
   app.html on 22 Aug, at the same time as five other features. The known
   memory problem — decodePcm() loading the whole recording at once, roughly
   659 MB for thirty minutes — predates the move and is unrelated to it. Fix
   that before assuming the relocation caused anything.
*/

import { onReady } from '../lib/ready.js';

export function initRecorder() {
  'use strict';

  const $ = id => document.getElementById(id);
  const wrap = $('rec-wrap');
  if (!wrap) return;   // now only true if the markup is genuinely absent

  const esc = s => (typeof escapeHtml === 'function'
    ? escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])));

  const fmt = ms => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, '0');
    return h ? h + ':' + String(m).padStart(2, '0') + ':' + ss : m + ':' + ss;
  };

  /* =======================================================================
     AUDIO STORE

     Keys are "<sessionId>:<seq padded to 6>". The padding is load-bearing:
     without it chunk 10 sorts before chunk 2 and the recording reassembles
     scrambled — and it still plays, which is what would make that dangerous.
     ======================================================================= */
  const DB_NAME = 'otterlabs-audio', DB_VER = 1, STORE = 'chunks';
  let dbp = null;

  function db() {
    if (dbp) return dbp;
    dbp = new Promise((res, rej) => {
      let r;
      try { r = indexedDB.open(DB_NAME, DB_VER); } catch (e) { rej(e); return; }
      r.onupgradeneeded = () => {
        const d = r.result;
        if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: 'k' });
      };
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
    return dbp;
  }
  const keyFor = (sid, seq) => sid + ':' + String(seq).padStart(6, '0');
  const RANGE = sid => IDBKeyRange.bound(sid + ':', sid + ':￿');

  async function putChunk(sid, seq, blob, type) {
    const d = await db();
    return new Promise((res, rej) => {
      const tx = d.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ k: keyFor(sid, seq), sessionId: sid, seq, blob, type });
      tx.oncomplete = res; tx.onerror = () => rej(tx.error);
    });
  }
  async function readChunks(sid) {
    const d = await db();
    return new Promise((res, rej) => {
      const out = [];
      const tx = d.transaction(STORE, 'readonly');
      const cur = tx.objectStore(STORE).openCursor(RANGE(sid));
      cur.onsuccess = e => { const c = e.target.result; if (c) { out.push(c.value); c.continue(); } else res(out); };
      cur.onerror = () => rej(cur.error);
    });
  }
  async function dropSession(sid) {
    const d = await db();
    return new Promise((res, rej) => {
      const tx = d.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const cur = store.openCursor(RANGE(sid));
      cur.onsuccess = e => { const c = e.target.result; if (c) { store.delete(c.primaryKey); c.continue(); } };
      tx.oncomplete = res; tx.onerror = () => rej(tx.error);
    });
  }

  /* Reached by purgeLocalDataOnSignOut(). Deleting the whole database rather
     than sweeping it: a shared laptop should keep nothing, and a cursor sweep
     can be interrupted halfway. */
  window.otterPurgeAudio = function otterPurgeAudio() {
    return new Promise(res => {
      try { dbp = null; const r = indexedDB.deleteDatabase(DB_NAME); r.onsuccess = r.onerror = r.onblocked = () => res(); }
      catch (e) { res(); }
    });
  };
  window.otterDropSessionAudio = dropSession;

  /* =======================================================================
     STATE

     One state at a time, and every screen is a function of it. The first
     version of this managed the same thing with scattered hidden/disabled
     toggles on individual buttons, which is how you end up with a Pause
     button visible next to a Start button.
     ======================================================================= */
  const IDLE = 'idle', RECORDING = 'recording', PAUSED = 'paused', FINISHING = 'finishing',
        TRANSCRIBING = 'transcribing', CHECKING = 'quality_check', SUMMARIZING = 'summarizing',
        READY = 'ready', TR_ERR = 'transcription_error', SUM_ERR = 'summary_error';

  const BUSY = { finishing: 1, transcribing: 1, quality_check: 1, summarizing: 1 };
  const WORDS = {
    idle: 'Ready',
    recording: 'Listening…',
    paused: 'Paused',
    finishing: 'Finishing recording…',
    transcribing: 'Transcribing…',
    quality_check: 'Checking the recording…',
    summarizing: 'Organizing notes…',
    ready: '',
    transcription_error: '',
    summary_error: ''
  };

  let st = IDLE;
  let note = null;
  let segs = [];
  let summary = null;

  /* The client chosen on the Ready screen, before any document exists. It is
     applied at creation time, so choosing a client still writes nothing. */
  let pendingClient = null;

  const hasAudio = () => !!(note && note.durationSec && note.audioOnDevice);
  const isDone = () => !!(note && (note.summaryHtml || note.transcriptState === 'ready'
                       || note.transcriptState === 'short' || segs.length));

  function setState(next) {
    st = next;
    // Read by idleBusy(). Transcribing and summarising involve no keyboard at
    // all, and signing someone out partway through would throw away the work
    // and leave them staring at a document that never arrived.
    window.recBusy = !!BUSY[next];
    render();
  }

  /* =======================================================================
     RECORDING
     ======================================================================= */
  const MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/ogg;codecs=opus'];
  function pickMime() {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return '';
    for (const m of MIMES) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) {} }
    return '';
  }

  let rec = null, stream = null, seq = 0, mime = '';
  let startedMs = 0, bankedMs = 0, tick = null, wake = null;

  const elapsed = () => bankedMs + (startedMs ? Date.now() - startedMs : 0);
  window.recActive = false;

  async function wakeOn() {
    try { if (navigator.wakeLock && !wake) wake = await navigator.wakeLock.request('screen'); } catch (e) {}
  }
  function wakeOff() { try { wake && wake.release(); } catch (e) {} wake = null; }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && st === RECORDING) wakeOn();
  });

  function warn(msg) {
    const el = $('rec-warn');
    if (!msg) { el.hidden = true; el.textContent = ''; return; }
    el.hidden = false; el.textContent = msg;
  }

  async function start() {
    if (st !== IDLE) return;
    /* A finished session is closed. Starting again would restart the chunk
       sequence at zero and overwrite the existing chunks in place — and the
       result still plays, so nobody would notice until the transcript came
       out wrong. Deleting the audio is the only way back here. */
    if (hasAudio()) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) {
      warn('This browser cannot record audio. Chrome, Edge, or Safari 15 and later can.');
      return;
    }
    if (!window.isSecureContext) { warn('Recording needs a secure connection. Open the app over https.'); return; }

    warn('');
    $('rec-state').textContent = 'Asking for the microphone…';
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
    } catch (e) {
      const denied = e && (e.name === 'NotAllowedError' || e.name === 'SecurityError');
      warn(denied
        ? 'The microphone is blocked for this site. Allow it in the address bar, then press Start again.'
        : 'Could not open the microphone: ' + ((e && e.message) || e));
      render();
      return;
    }

    mime = pickMime();
    try { rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : undefined); }
    catch (e) { warn('Could not start recording: ' + ((e && e.message) || e)); stopTracks(); render(); return; }
    mime = rec.mimeType || mime;

    /* THE DOCUMENT IS CREATED HERE AND NOWHERE EARLIER — after the microphone
       is open and the recorder has actually started, so a denied permission or
       an unsupported browser leaves nothing behind either. */
    if (!note) {
      try {
        note = await notesCreateSessionDoc(pendingClient);
      } catch (e) {
        console.warn('could not create session:', e);
        warn('Could not start this conversation: ' + ((e && e.message) || e));
        rec = null; stopTracks(); render(); return;
      }
    }

    seq = 0; bankedMs = 0; startedMs = Date.now();
    const sid = note.id;

    rec.ondataavailable = ev => {
      if (!ev.data || !ev.data.size) return;
      // Captured now rather than inside the promise: two chunks landing close
      // together would otherwise race for the same sequence number.
      const n = seq++;
      putChunk(sid, n, ev.data, mime).catch(err => {
        console.warn('audio chunk not stored:', err);
        warn('Running out of room to store this recording. Stop and free some space.');
      });
    };
    rec.onerror = ev => {
      console.warn('recorder error:', ev && ev.error);
      warn('The recorder stopped unexpectedly. Everything captured so far is saved.');
      stop();
    };

    // Five-second slices: a crashed tab costs five seconds, not a home visit.
    rec.start(5000);
    window.recActive = true;
    wakeOn();
    tapStart(stream);
    livePreviewStart();
    patch({ sessionState: 'recording', audioMime: mime, startedAt: new Date().toISOString() });
    if (tick) clearInterval(tick);
    tick = setInterval(() => { $('rec-time').textContent = fmt(elapsed()); }, 500);
    setState(RECORDING);
  }

  function pauseResume() {
    if (!rec) return;
    if (rec.state === 'recording') {
      rec.pause();
      bankedMs += Date.now() - startedMs; startedMs = 0;
      setState(PAUSED);
    } else if (rec.state === 'paused') {
      rec.resume();
      startedMs = Date.now();
      setState(RECORDING);
    }
  }

  function stopTracks() {
    try { stream && stream.getTracks().forEach(t => t.stop()); } catch (e) {}
    stream = null;
  }

  async function stop() {
    if (!rec) return;
    setState(FINISHING);
    const finalMs = elapsed();
    const done = new Promise(res => { rec.onstop = res; });
    try { if (rec.state !== 'inactive') rec.stop(); } catch (e) {}
    await done.catch(() => {});
    if (tick) { clearInterval(tick); tick = null; }
    livePreviewStop(); tapStop(); stopTracks(); wakeOff();
    window.recActive = false;
    rec = null; startedMs = 0; bankedMs = finalMs;

    const secs = Math.round(finalMs / 1000);

    /* Start then Stop a second later is not a conversation. Discard it rather
       than putting a blank card in the library and making somebody work out
       what it was and whether it is safe to delete. */
    if (secs < MIN_USEFUL_SEC) {
      const dead = note;
      note = null; bankedMs = 0;
      if (dead) await discardSession(dead);
      warn('That was too short to transcribe. Press Start when you are ready.');
      st = IDLE;
      render();
      return;
    }

    patch({
      sessionState: 'stopped',
      endedAt: new Date().toISOString(),
      durationSec: secs,
      audioOnDevice: true,
      audioDeviceLabel: deviceLabel(),
      transcriptState: 'none'
    });
    // Straight through. No "would you like us to transcribe this?" — the
    // answer is always yes, and asking it mid-conversation is the worst
    // possible moment to ask anything.
    await transcribe();
  }

  function deviceLabel() {
    const ua = navigator.userAgent || '';
    if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad';
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/Android/.test(ua)) return 'Android';
    if (/Macintosh/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows PC';
    return 'this device';
  }

  /* =======================================================================
     THE WAVEFORM

     The confirmation that the app is hearing them, and on this screen it is
     the main thing. Drawn on a canvas because it repaints every frame; DOM
     bars at 60fps are a different kind of expensive.

     It calms rather than stops when the room goes quiet, and freezes flat
     when paused, so the difference between "listening to silence" and "not
     listening" is visible at a glance.
     ======================================================================= */
  const BARS = 44;
  let levels = new Array(BARS).fill(0);
  let waveRaf = 0, analyser = null, freq = null, waveColors = null;

  function readColors() {
    const cs = getComputedStyle(document.body);
    waveColors = {
      live: (cs.getPropertyValue('--text') || '#1c1c24').trim(),
      calm: (cs.getPropertyValue('--muted') || '#64748b').trim()
    };
  }

  function waveLoop() {
    waveRaf = requestAnimationFrame(waveLoop);
    const c = $('rec-wave');
    if (!c || !c.clientWidth) return;
    const ctx = c.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth, h = c.clientHeight;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (!waveColors) readColors();

    const target = new Array(BARS).fill(0);
    if (analyser && st === RECORDING) {
      analyser.getByteFrequencyData(freq);
      // Only the lower half of the spectrum — speech lives there, and the top
      // end is mostly hiss that makes the bars twitch for no reason.
      const usable = Math.floor(freq.length * 0.55);
      const step = Math.max(1, Math.floor(usable / BARS));
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        for (let j = i * step; j < (i + 1) * step; j++) sum += freq[j] || 0;
        target[i] = Math.min(1, (sum / step) / 132);
      }
    }
    const ease = st === RECORDING ? 0.38 : 0.10;
    for (let i = 0; i < BARS; i++) levels[i] += (target[i] - levels[i]) * ease;

    const gap = 4;
    const bw = Math.max(2, (w - gap * (BARS - 1)) / BARS);
    const mid = h / 2, maxH = h - 8;
    ctx.fillStyle = st === RECORDING ? waveColors.live : waveColors.calm;
    for (let i = 0; i < BARS; i++) {
      const bh = Math.max(2.5, levels[i] * maxH);
      const x = i * (bw + gap), y = mid - bh / 2;
      ctx.globalAlpha = st === RECORDING ? (0.35 + levels[i] * 0.65) : 0.32;
      if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, bw, bh, bw / 2); ctx.fill(); }
      else ctx.fillRect(x, y, bw, bh);
    }
    ctx.globalAlpha = 1;
  }

  /* =======================================================================
     THE LIVE TRANSCRIPT TAP

     Whisper is a batch process, and MediaRecorder's chunks cannot be decoded
     independently — only the first carries the container header, so chunks
     12 through 18 on their own are not a file. That is why this exists: a
     SECOND path off the same microphone that keeps raw samples in a ring
     buffer, with no container involved, so any window can be transcribed at
     any moment.

     ScriptProcessorNode rather than an AudioWorklet: it is deprecated but
     universally supported including on iPadOS, and the work it does here is
     copying floats. The expensive part happens in the worker either way.

     Everything this produces is THROWAWAY. At Stop, the authoritative pass
     runs over the complete recording with the settings the spike validated.
     The live line never becomes the transcript.
     ======================================================================= */
  /* LIVE PREVIEW IS OFF ON iPad AND iPhone, DELIBERATELY.

     It is the most expensive thing on this screen by a wide margin: it loads
     the whole Whisper model onto the GPU and runs inference every 25 seconds
     WHILE MediaRecorder is writing and the waveform is animating. A Mac
     absorbs that. An iPad running the same thing reloaded the page mid-visit,
     which is the worst failure this feature has — the recording is the part
     that cannot be redone.

     Note what the spike proved and what it did not: transcribing a finished
     recording on the iPad is fast and stable (0.065x real time). Transcribing
     while simultaneously recording is a different workload, and it was never
     tested. The authoritative pass at Stop is unaffected and still runs
     locally on the device.

     So: Mac gets the fastest experience, iPad gets the one that survives. The
     waveform and the timer still show that the app is listening, which is what
     that line was there to reassure people about. */
  const IS_IOS = (function () {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  })();
  // A low-memory machine of any kind gets the same treatment.
  const LIVE_PREVIEW = !IS_IOS && !(navigator.deviceMemory && navigator.deviceMemory <= 4);

  const RING_SEC = LIVE_PREVIEW ? 40 : 8, WIN_SEC = 30, STEP_MS = 25000;
  let tapCtx = null, tapNode = null, ring = null, ringLen = 0, ringRate = 0, ringWrite = 0, ringFilled = 0;
  let previewTimer = null, previewBusy = false, liveText = '', livePrimed = false;

  function tapStart(src) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      tapCtx = new AC();
      const node = tapCtx.createMediaStreamSource(src);
      analyser = tapCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.72;
      node.connect(analyser);
      freq = new Uint8Array(analyser.frequencyBinCount);

      /* Without live preview there is nothing to feed, so the ring buffer and
         the ScriptProcessor are skipped entirely — the analyser above is all
         the waveform needs. That removes a deprecated main-thread node from
         the recording path on exactly the devices that were struggling. */
      if (!LIVE_PREVIEW) return;

      ringRate = tapCtx.sampleRate;
      ringLen = Math.ceil(RING_SEC * ringRate);
      ring = new Float32Array(ringLen); ringWrite = 0; ringFilled = 0;

      tapNode = tapCtx.createScriptProcessor(4096, 1, 1);
      tapNode.onaudioprocess = e => {
        if (st !== RECORDING) return;
        const d = e.inputBuffer.getChannelData(0);
        for (let i = 0; i < d.length; i++) { ring[ringWrite] = d[i]; ringWrite = (ringWrite + 1) % ringLen; }
        ringFilled = Math.min(ringLen, ringFilled + d.length);
      };
      node.connect(tapNode);
      // A ScriptProcessor only fires when it reaches a destination. Route it
      // through a silent gain so nothing is played back into the room.
      const mute = tapCtx.createGain();
      mute.gain.value = 0;
      tapNode.connect(mute); mute.connect(tapCtx.destination);
    } catch (e) { console.warn('audio tap unavailable:', e); }
  }

  function tapStop() {
    try { if (tapNode) { tapNode.onaudioprocess = null; tapNode.disconnect(); } } catch (e) {}
    try { tapCtx && tapCtx.close(); } catch (e) {}
    tapNode = null; tapCtx = null; analyser = null; freq = null; ring = null; ringFilled = 0;
  }

  function ringTail(sec) {
    const n = Math.min(ringFilled, Math.ceil(sec * ringRate));
    const out = new Float32Array(n);
    let p = (ringWrite - n + ringLen) % ringLen;
    for (let i = 0; i < n; i++) { out[i] = ring[p]; p = (p + 1) % ringLen; }
    return out;
  }

  function downTo16k(data, from) {
    const to = 16000;
    if (!data.length || from === to) return data;
    const ratio = from / to, n = Math.floor(data.length / ratio);
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const p = i * ratio, i0 = Math.floor(p), i1 = Math.min(i0 + 1, data.length - 1);
      out[i] = data[i0] + (data[i1] - data[i0]) * (p - i0);
    }
    return out;
  }

  function livePreviewStart() {
    liveText = ''; livePrimed = false;
    $('rec-live').textContent = '';
    if (previewTimer) clearInterval(previewTimer);
    if (!LIVE_PREVIEW) { console.log('[rec] live preview disabled on this device'); return; }
    previewTimer = setInterval(runPreview, STEP_MS);
  }
  function livePreviewStop() {
    if (previewTimer) clearInterval(previewTimer);
    previewTimer = null; previewBusy = false;
  }

  async function runPreview() {
    if (previewBusy || st !== RECORDING || !ring || ringFilled < ringRate * 8) return;
    previewBusy = true;
    try {
      const pcm = downTo16k(ringTail(WIN_SEC), ringRate);
      const text = await workerPreview(pcm);
      if (st === RECORDING) appendLive(text);
    } catch (e) {
      /* A failed preview is cosmetic and must never interrupt the recording —
         and after one failure it stops trying. Whatever went wrong is likely
         to keep going wrong, and retrying every 25 seconds turns a cosmetic
         problem into a repeated memory spike underneath a live recording. */
      console.warn('live preview stopped after an error:', e);
      livePreviewStop();
    } finally { previewBusy = false; }
  }

  /* Appends only settled text. Each window overlaps the last, so the opening
     sentence is almost always something already shown — dropping it is what
     keeps the line from rewriting itself while somebody is trying to talk. */
  function appendLive(text) {
    let t = (text || '').trim();
    if (!t) return;
    if (livePrimed) {
      const m = t.match(/^[^.?!]*[.?!]\s+/);
      if (m && m[0].length < t.length) t = t.slice(m[0].length);
    }
    livePrimed = true;
    liveText = (liveText + ' ' + t).trim();
    const el = $('rec-live');
    el.hidden = false;
    el.textContent = '…' + liveText.slice(-280);
  }

  /* =======================================================================
     CRASH FORENSICS

     When WebKit kills a tab there is no error, no unload event and no console
     left to read — the page simply reappears. So the only way to learn where
     it died is to have written it down BEFORE each expensive step.

     localStorage, not IndexedDB, and that is the whole point: setItem is
     synchronous and has hit disk by the time it returns. An IndexedDB
     transaction is async and is exactly the thing a process kill loses.

     Read it after a crash with otterRecLog().
     ======================================================================= */
  const DIAG_KEY = 'mt.recDiag';
  let diag = null, heartbeat = null;

  function diagFlush() {
    try { localStorage.setItem(DIAG_KEY, JSON.stringify(diag)); } catch (e) {}
  }
  function diagStart(info) {
    diag = {
      sessionId: (note && note.id) || null,
      startedAt: new Date().toISOString(),
      t0: Date.now(),
      stage: 'start',
      complete: false,
      marks: [],
      ...recDiag(),
      ...info
    };
    diagFlush();
    if (heartbeat) clearInterval(heartbeat);
    // A stale heartbeat separates "killed mid-run" from "closed the tab".
    heartbeat = setInterval(() => { if (diag) { diag.beat = Date.now(); diagFlush(); } }, 2000);
  }
  function mark(stage, extra) {
    if (!diag) return;
    diag.stage = stage;
    diag.beat = Date.now();
    diag.marks.push({ stage, ms: Date.now() - diag.t0, ...(extra || {}) });
    diagFlush();
    console.log('[rec]', stage, extra || '');
  }
  function diagEnd(ok) {
    if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
    if (!diag) return;
    diag.stage = ok ? 'transcription_complete' : 'failed';
    diag.complete = true;
    diagFlush();
  }

  window.otterRecLog = function otterRecLog() {
    let d = null;
    try { d = JSON.parse(localStorage.getItem(DIAG_KEY) || 'null'); } catch (e) {}
    if (!d) { console.log('No transcription diagnostics recorded.'); return null; }
    console.log('%cLast transcription run', 'font-weight:600');
    console.log('device:', d.device, '| backend:', d.backend, '| webgpu:', d.webgpu,
      '| livePreview:', d.livePreview, '| memory:', d.deviceMemory);
    console.log('audio:', d.durationSec + 's', '| chunks:', d.chunks, '| bytes:', d.bytes);
    console.log(d.complete ? 'Finished: ' + d.stage
      : '%cDIED AT: ' + d.stage + ' — the page was killed before this step finished.',
      d.complete ? '' : 'color:#b91c1c;font-weight:600');
    console.table(d.marks);
    return d;
  };

  /* Called once at startup. If the last run never reached completion, the tab
     was killed rather than closed, and there is a recording still sitting on
     this device that never got transcribed. */
  function diagCheckPrevious() {
    let d = null;
    try { d = JSON.parse(localStorage.getItem(DIAG_KEY) || 'null'); } catch (e) {}
    if (!d || d.complete) return null;
    console.warn('[rec] previous transcription ended unexpectedly at:', d.stage, d);
    return d;
  }
  window.otterRecUnfinished = diagCheckPrevious;

  /* =======================================================================
     BACKEND CHOICE

     Mac gets the fast path, iPad gets the one that survives. The user never
     sees either word — the screen says "Transcribing…" whichever it is.

     Override for A/B testing on a real device:
       otterSetBackend('webgpu' | 'wasm' | 'auto')
     ======================================================================= */
  const BACKEND_KEY = 'mt.recBackend';
  function preferredBackend() {
    let forced = '';
    try { forced = localStorage.getItem(BACKEND_KEY) || ''; } catch (e) {}
    if (forced === 'wasm' || forced === 'webgpu') return forced;
    /* iOS defaults to wasm. WebGPU on iPadOS is where the page is being killed,
       and a slow transcript beats a lost recording every time. */
    return IS_IOS ? 'wasm' : 'webgpu';
  }
  window.otterSetBackend = function (v) {
    try {
      if (v === 'auto') localStorage.removeItem(BACKEND_KEY);
      else localStorage.setItem(BACKEND_KEY, v);
    } catch (e) {}
    // The worker holds a loaded model, so it has to go for the change to apply.
    if (worker) { try { worker.terminate(); } catch (e) {} worker = null; }
    console.log('Backend set to', v, '— now:', preferredBackend(), '(worker reset)');
  };

  /* =======================================================================
     THE WORKER
     ======================================================================= */
  let worker = null, pending = null, previewPending = null;

  function getWorker() {
    if (worker) return worker;
    // Same-origin, type:module. It imports the vendored library, which is why
    // there is no CDN anywhere in this path.
    worker = new Worker('/whisper-worker.js', { type: 'module' });
    worker.onmessage = e => {
      const d = e.data || {};
      if (d.type === 'stage') {
        // Model load is the biggest single allocation, so both ends of it are
        // written down before and after.
        if (d.stage === 'model_load_start' || d.stage === 'model_load_complete') mark(d.stage, { device: d.device });
        progress(d.pct, null);
        return;
      }
      if (d.type === 'modelReady') return;
      if (d.type === 'preview' && previewPending) { const p = previewPending; previewPending = null; p.res(d.text || ''); return; }
      if (d.type === 'done' && pending) { const p = pending; pending = null; p.res(d); return; }
      if (d.type === 'error') {
        const err = new Error(d.error || 'transcription failed');
        if (previewPending) { const p = previewPending; previewPending = null; p.rej(err); return; }
        if (pending) { const p = pending; pending = null; p.rej(err); }
      }
    };
    worker.onerror = err => {
      const e = new Error((err && err.message) || 'the transcription engine failed to start');
      if (previewPending) { const p = previewPending; previewPending = null; p.rej(e); }
      if (pending) { const p = pending; pending = null; p.rej(e); }
      try { worker.terminate(); } catch (x) {}
      worker = null;
    };
    return worker;
  }

  function workerRun(pcm) {
    return new Promise((res, rej) => {
      pending = { res, rej };
      // TRANSFERRED, not copied — the buffer is detached here rather than
      // duplicated, so the samples exist once, not twice.
      getWorker().postMessage({ cmd: 'transcribe', pcm, device: preferredBackend() }, [pcm.buffer]);
    });
  }
  function workerPreview(pcm) {
    return new Promise((res, rej) => {
      previewPending = { res, rej };
      getWorker().postMessage({ cmd: 'preview', pcm, device: preferredBackend() }, [pcm.buffer]);
    });
  }

  /* =======================================================================
     DECODE
     ======================================================================= */
  function toMono(buf) {
    if (buf.numberOfChannels === 1) return buf.getChannelData(0);
    const n = buf.length, out = new Float32Array(n), ch = buf.numberOfChannels;
    for (let c = 0; c < ch; c++) {
      const d = buf.getChannelData(c);
      for (let i = 0; i < n; i++) out[i] += d[i] / ch;
    }
    return out;
  }

  async function decodePcm(blob) {
    const TARGET = 16000;
    const bytes = await blob.arrayBuffer();
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) throw new Error('This browser cannot decode audio.');
    const ac = new AC();
    let decoded;
    try {
      decoded = await new Promise((res, rej) => {
        // Callback form as well as promise form: Safari's promise form is
        // unreliable on MP4 produced by its own MediaRecorder.
        const p = ac.decodeAudioData(bytes, res, rej);
        if (p && typeof p.then === 'function') p.then(res, rej);
      });
    } finally { try { ac.close(); } catch (e) {} }

    const duration = decoded.duration;
    const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (OAC) {
      try {
        const off = new OAC(1, Math.max(1, Math.ceil(duration * TARGET)), TARGET);
        const src = off.createBufferSource();
        src.buffer = decoded; src.connect(off.destination); src.start();
        const out = await off.startRendering();
        return { pcm: out.getChannelData(0), duration };
      } catch (e) { console.warn('OfflineAudioContext resample failed, using linear:', e); }
    }
    return { pcm: downTo16k(toMono(decoded), decoded.sampleRate), duration };
  }

  /* =======================================================================
     THE COVERAGE GATE

     MECHANICAL, AND IT REFUSES RATHER THAN WARNS. During the spike, small.en
     produced a transcript of a ten-minute conversation covering one minute of
     it, and it read perfectly — fluent, plausible, missing ninety per cent of
     what was said. A summary built on that would have been confidently and
     invisibly wrong, and no prompt instructing the model to "be careful"
     would have caught it.

     Two measures because they fail differently. REACH catches a transcript
     that stops early. The largest internal GAP catches one that reaches the
     end while quietly dropping a window in the middle — the shape that would
     otherwise sail through. Ordinary silence is seconds; a dropped decode
     window is minutes.
     ======================================================================= */
  const GATE_REACH = 0.90, GATE_GAP_S = 120;

  function coverageOf(list, audioSec) {
    if (!list.length) return { ok: false, reach: 0, gap: 0, why: 'nothing was transcribed' };
    if (!audioSec) return { ok: true, reach: 1, gap: 0, why: '' };
    let last = 0, prevEnd = 0, gap = 0;
    list.forEach(s => {
      const a = typeof s.start === 'number' ? s.start : null;
      const b = typeof s.end === 'number' ? s.end : a;
      if (a !== null) gap = Math.max(gap, a - prevEnd);
      if (b !== null) { prevEnd = Math.max(prevEnd, b); last = Math.max(last, b); }
    });
    gap = Math.max(gap, audioSec - last);
    const reach = Math.min(1, last / audioSec);
    return {
      ok: reach >= GATE_REACH && gap <= GATE_GAP_S,
      reach, gap,
      why: reach < GATE_REACH ? 'it stops early' : (gap > GATE_GAP_S ? 'a stretch in the middle is missing' : '')
    };
  }

  /* =======================================================================
     SEGMENT STORAGE
     ======================================================================= */
  const segIdOf = i => 's' + String(i).padStart(6, '0');

  async function segSave(noteId, list) {
    if (!notesTeamMode()) {
      const data = notesLoadPersonal();
      const n = data.notes.find(x => x.id === noteId);
      if (n) { n.segmentsJson = JSON.stringify(list); notesSavePersonal(data); }
      return;
    }
    const col = fbDb.collection('teams').doc(activeTeamId).collection('notes').doc(noteId).collection('segments');
    // Clear the previous run first, or a shorter re-transcription leaves the
    // tail of the old one behind and nothing looks wrong.
    const old = await col.get();
    const stale = [];
    old.forEach(d => stale.push(d.ref));
    for (let i = 0; i < stale.length; i += 400) {
      const b = fbDb.batch();
      stale.slice(i, i + 400).forEach(r => b.delete(r));
      await b.commit();
    }
    for (let i = 0; i < list.length; i += 400) {
      const b = fbDb.batch();
      list.slice(i, i + 400).forEach((s, j) => {
        b.set(col.doc(segIdOf(i + j)), { seq: i + j, start: s.start, end: s.end, text: s.text });
      });
      await b.commit();
    }
  }

  async function segLoad(noteId) {
    if (!notesTeamMode()) {
      try { const v = JSON.parse((note && note.segmentsJson) || '[]'); return Array.isArray(v) ? v : []; }
      catch (e) { return []; }
    }
    try {
      const snap = await fbDb.collection('teams').doc(activeTeamId)
        .collection('notes').doc(noteId).collection('segments').orderBy('seq').get();
      const out = [];
      snap.forEach(d => out.push(d.data()));
      return out;
    } catch (e) { console.warn('could not load transcript segments:', e); return []; }
  }

  /* =======================================================================
     SAVING
     ======================================================================= */
  let saveTimer = null;

  function patch(fields) {
    if (!note) return;
    Object.assign(note, fields);
    if (typeof notesPatchActive === 'function') notesPatchActive(fields, true);
  }
  function queueSave() {
    clearTimeout(saveTimer);
    $('rec-saved').textContent = '';
    saveTimer = setTimeout(flushSave, 900);
  }
  function flushSave() {
    clearTimeout(saveTimer); saveTimer = null;
    if (!note || !isDone()) return;
    const html = $('rec-sections').innerHTML;
    const text = $('rec-sections').innerText;
    const title = ($('rec-doc-title').textContent || '').trim();
    patch({ summaryHtml: html, body: text, title: title || note.title });
    $('rec-saved').textContent = 'Saved';
  }

  /* Lost in a rewrite and still called from two places, which a syntax check
     cannot see — the app parsed perfectly and threw on the first iPad that
     recorded silence. This is why "it compiles" is not the bar after a large
     replacement. */
  function progressDone() {
    $('rec-prog').hidden = true;
    $('rec-prog-fill').style.width = '0%';
  }

  function progress(pct, sub) {
    $('rec-prog').hidden = false;
    if (typeof pct === 'number') $('rec-prog-fill').style.width = Math.max(0, Math.min(100, pct)) + '%';
    if (sub) $('rec-prog-sub').textContent = sub;
  }

  /* =======================================================================
     THE FLOW: stop → transcribe → check → summarise → document
     ======================================================================= */
  let gateText = '';
  const SHORT_MSG = 'We could not capture the whole conversation. Part of the recording may be missing, so we have not turned it into notes.';

  async function transcribe() {
    if (!note) return;
    const id = note.id;
    if (!hasAudio()) { gateText = 'There is no audio on this device to work from.'; setState(TR_ERR); return; }

    gateText = '';
    setState(TRANSCRIBING);
    progress(2, 'Turning your conversation into notes');

    try {
      diagStart({ backend: preferredBackend() });
      mark('recording_complete', { durationSec: note.durationSec });

      const parts = await readChunks(id);
      if (!parts.length) throw new Error('The audio for this session is not on this device.');
      const bytes = parts.reduce((n, c) => n + ((c.blob && c.blob.size) || 0), 0);
      mark('chunks_read', { chunks: parts.length, bytes });
      if (diag) { diag.chunks = parts.length; diag.bytes = bytes; diagFlush(); }

      const blob = new Blob(parts.map(c => c.blob), { type: note.audioMime || parts[0].type || 'audio/webm' });

      progress(6, 'Turning your conversation into notes');
      mark('audio_decode_start');
      const { pcm, duration } = await decodePcm(blob);
      mark('audio_decode_complete', { samples: pcm.length, seconds: Math.round(duration) });

      /* Two independent measurements of how long this recording is: what the
         decoder says, and the wall clock the recorder kept. MediaRecorder
         writes no duration into a WebM header, so the container's own answer
         can be wrong by half — and this is the number the coverage gate is a
         comparison against. */
      let audioSec = duration;
      if (!isFinite(audioSec) || audioSec <= 0) audioSec = note.durationSec || 0;
      else if (note.durationSec && Math.abs(audioSec - note.durationSec) / note.durationSec > 0.25) {
        console.warn('decoded duration', audioSec, 'disagrees with recorded', note.durationSec);
        audioSec = Math.max(audioSec, note.durationSec);
      }

      mark('inference_start');
      const res = await workerRun(pcm);
      mark('inference_complete', { segments: (res.chunks || []).length, ms: Math.round(res.ms || 0) });
      segs = (res.chunks || []).filter(s => s.text);

      /* Whisper heard nothing usable — a muted microphone, a recording made in
         a pocket, a room with nobody talking. There is no conversation here to
         make a document out of, so none is made. */
      const spoken = (res.text || '').split(/\s+/).filter(Boolean).length;
      if (!segs.length || spoken < 3) {
        const dead = note;
        note = null; segs = []; bankedMs = 0;
        progressDone();
        if (dead) await discardSession(dead);
        warn('No speech was detected in that recording. Nothing was saved — press Start to try again.');
        st = IDLE;
        render();
        return;
      }

      setState(CHECKING);
      const cov = coverageOf(segs, audioSec);
      const words = (res.text || '').split(/\s+/).filter(Boolean).length;

      await segSave(id, segs);
      mark('transcript_saved', { segments: segs.length, words });
      diagEnd(true);
      patch({
        transcriptState: cov.ok ? 'ready' : 'short',
        transcriptWords: words,
        transcriptReach: Math.round(cov.reach * 100),
        transcriptGapSec: Math.round(cov.gap),
        transcriptModel: 'whisper-base.en',
        transcriptAt: new Date().toISOString()
      });

      if (!cov.ok) {
        // Refuses rather than warns. Leaving an incomplete transcript
        // available with a caution beside it would make the caution the only
        // thing preventing a confidently wrong summary.
        gateText = SHORT_MSG;
        setState(TR_ERR);
        return;
      }
      await summarize();

    } catch (e) {
      console.warn('transcription failed:', e, recDiag());
      mark('failed', { error: String((e && e.message) || e).slice(0, 200) });
      diagEnd(false);
      patch({ transcriptState: 'failed', transcriptError: String((e && e.message) || e).slice(0, 300) });
      /* The reason goes ON SCREEN. It used to be swallowed into "we could not
         transcribe this recording", which is unactionable anywhere and
         completely unactionable on an iPad, where there is no console to open.
         A CHW will not read it; the person debugging it needs it. */
      gateText = 'We could not transcribe this recording — ' + ((e && e.message) || e) +
                 '. The audio is untouched, so nothing is lost.';
      setState(TR_ERR);
    }
  }

  /* What this device actually is, and what the transcription stack resolved to
     on it. Attached to failures so a Mac run and an iPad run can be compared
     with real data rather than guesses. Read it any time with otterRecDiag(). */
  function recDiag() {
    const ua = navigator.userAgent || '';
    return {
      device: deviceLabel(),
      ua: ua.slice(0, 160),
      webgpu: !!navigator.gpu,
      livePreview: LIVE_PREVIEW,
      deviceMemory: navigator.deviceMemory || null,
      secure: window.isSecureContext,
      moduleWorker: (function () {
        // Safari only gained module workers in 15; without them the worker
        // never starts and every transcription fails at the same point.
        try { new Worker('data:text/javascript,', { get type() { return 'module'; } }); return true; }
        catch (e) { return false; }
      })(),
      audioMime: note && note.audioMime,
      durationSec: note && note.durationSec,
      segments: segs.length,
      memoryMB: (performance.memory && Math.round(performance.memory.usedJSHeapSize / 1048576)) || null,
      transcriptError: note && note.transcriptError
    };
  }
  window.otterRecDiag = recDiag;

  /* =======================================================================
     SUMMARY COVERAGE — does the document actually represent the conversation?

     A transcript can pass the audio coverage gate and still produce notes that
     quietly drop half of what was discussed. That happened on the first real
     test: a minute-long recording covering a software lawsuit and then a new
     cat produced notes about the cat only. Nothing was broken — the transcript
     was complete and the whole thing reached the model — the prompt simply had
     no instruction to be complete, and plenty of instruction to be brief.

     THIS COSTS NOTHING. Every bullet already carries sourceSegIds, because
     traceability has to be produced at the moment the model decides what a
     bullet says. Reusing those ids as a coverage measure is free: no second
     request, no extra tokens, just arithmetic on data we already required.

     What it looks for is a CONTIGUOUS stretch of transcript that no bullet
     cites. Scattered uncited segments are normal — filler, greetings, repeated
     sentences. A long unbroken run is a subject that vanished. */
  const SUMMARY_GAP_SHARE = 0.30;   // an uncited run this share of the words is a dropped topic

  function summaryCoverage(sum, list) {
    if (!list.length) return { ok: true, share: 0, from: 0, to: 0 };
    const cited = new Set();
    (sum.sections || []).forEach(sec => (sec.bullets || []).forEach(b => {
      (Array.isArray(b.sourceSegIds) ? b.sourceSegIds : []).forEach(id => {
        const n = parseInt(String(id).replace(/^s/, ''), 10);
        if (!isNaN(n)) cited.add(n);
      });
    }));

    // Weighted by words, not segment count: one long segment is more of the
    // conversation than three "okay"s.
    const words = list.map(s => (s.text || '').split(/\s+/).filter(Boolean).length);
    const total = words.reduce((a, b) => a + b, 0);
    if (!total) return { ok: true, share: 0, from: 0, to: 0 };

    let best = 0, bestFrom = 0, bestTo = 0, run = 0, runFrom = 0;
    for (let i = 0; i < list.length; i++) {
      if (cited.has(i)) { run = 0; continue; }
      if (run === 0) runFrom = i;
      run += words[i];
      if (run > best) { best = run; bestFrom = runFrom; bestTo = i; }
    }
    return {
      ok: (best / total) < SUMMARY_GAP_SHARE,
      share: best / total,
      from: (list[bestFrom] && list[bestFrom].start) || 0,
      to: (list[bestTo] && (list[bestTo].end || list[bestTo].start)) || 0
    };
  }

  /* THE ONLY THING THAT COSTS MONEY. One call, after Stop, and only once the
     gate has passed. Roughly 4,000 tokens in and 700 out for a half-hour
     conversation. Nothing is called while listening and nothing per segment —
     if that ever changes, so does the running cost of this feature. */
  async function summarize() {
    if (!segs.length) { gateText = 'There is no transcript to work from.'; setState(SUM_ERR); return; }
    setState(SUMMARIZING);
    progress(72, 'Organizing notes');
    try {
      const payload = segs.map((s, i) => '[' + segIdOf(i) + '] ' + s.text).join('\n');
      let out = await window.otterAI('summarizeSession', payload);
      if (typeof out === 'string') {
        try { out = JSON.parse(out); }
        catch (e) { throw new Error('The notes came back in a form we could not read.'); }
      }
      if (!out || !Array.isArray(out.sections) || !out.sections.length) throw new Error('The notes came back empty.');

      summary = out;
      patch({
        title: (out.title || '').trim() || note.title,
        summaryJson: JSON.stringify(out),
        summaryHtml: docHtml(out),
        body: docText(out),
        sessionState: 'done'
      });

      /* Checked locally and for free. A second request is NOT made
         automatically — that would double the cost of the feature to fix a
         problem that is usually the prompt's fault, not this conversation's.
         The user is told, and Rewrite notes is theirs to press. */
      const cov = summaryCoverage(out, segs);
      patch({ summaryGapShare: Math.round(cov.share * 100) });
      gateText = cov.ok ? '' :
        'These notes may be missing part of the conversation — nothing was written about ' +
        fmt(cov.from * 1000) + ' to ' + fmt(cov.to * 1000) +
        '. The full transcript is saved, so nothing is lost.';

      progress(100, 'Done');
      setState(READY);
    } catch (e) {
      console.warn('summary failed:', e);
      gateText = 'We could not organize the notes: ' + ((e && e.message) || e) +
                 ' The transcript is saved, so nothing is lost.';
      setState(SUM_ERR);
    }
  }

  /* =======================================================================
     THE DOCUMENT

     Rendered from the model's JSON once, then edited as HTML. sourceSegIds
     ride along as data-seg attributes so traceability survives editing — the
     original JSON is kept too, but the ids have to live on the elements or
     they are lost the first time somebody fixes a typo.
     ======================================================================= */
  function docHtml(sum) {
    return (sum.sections || []).map(sec => {
      const bullets = (sec.bullets || []).map(b => {
        const ids = Array.isArray(b.sourceSegIds) && b.sourceSegIds.length
          ? ' data-seg="' + esc(b.sourceSegIds.join(',')) + '"' : '';
        const kids = Array.isArray(b.children) && b.children.length
          ? '<ul>' + b.children.map(c => '<li>' + esc(c) + '</li>').join('') + '</ul>' : '';
        return '<li' + ids + '>' + esc(b.text || '') + kids + '</li>';
      }).join('');
      return '<h3>' + esc(sec.heading || '') + '</h3><ul>' + bullets + '</ul>';
    }).join('');
  }

  function docText(sum) {
    return (sum.sections || []).map(sec => {
      const lines = (sec.bullets || []).map(b => {
        const kids = (b.children || []).map(c => '    - ' + c).join('\n');
        return '  • ' + (b.text || '') + (kids ? '\n' + kids : '');
      }).join('\n');
      return (sec.heading || '') + '\n' + lines;
    }).join('\n\n');
  }

  function metaLine() {
    if (!note) return '';
    const bits = [];
    const when = note.endedAt || note.createdAt || '';
    const d = when ? new Date(when) : null;
    if (d && !isNaN(d)) {
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      bits.push(sameDay ? 'Today' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
    }
    if (note.durationSec) bits.push(Math.max(1, Math.round(note.durationSec / 60)) + ' min');
    const cls = (typeof notesGetClients === 'function') ? notesGetClients(note) : [];
    if (cls.length && cls[0] && cls[0].name) bits.push(cls[0].name);
    return bits.join(' · ');
  }

  function renderSegs() {
    const el = $('rec-segs');
    if (!segs.length) { el.innerHTML = '<div class="rc-empty">No transcript for this session.</div>'; return; }
    el.innerHTML = segs.map((s, i) =>
      '<div class="rc-seg" id="' + segIdOf(i) + '"><b>' + fmt((s.start || 0) * 1000) + '</b><span>' + esc(s.text) + '</span></div>'
    ).join('');
  }

  function showPane(which) {
    $('rec-pane-notes').hidden = which !== 'notes';
    $('rec-pane-transcript').hidden = which !== 'transcript';
    [...$('rec-tabs').querySelectorAll('.rc-tab[data-pane]')].forEach(b =>
      b.classList.toggle('is-on', b.dataset.pane === which));
  }

  /* =======================================================================
     RENDER — every screen is a function of the state
     ======================================================================= */
  function render() {
    const onDoc = (st === READY || st === TR_ERR || st === SUM_ERR);
    $('rec-stage').hidden = onDoc;
    $('rec-doc').hidden = !onDoc;
    $('rec-share').hidden = !onDoc;
    $('rec-back-label').textContent = 'Documents';

    if (onDoc) {
      $('rec-bar-title').textContent = '';
      $('rec-doc-title').textContent = (note && note.title) || '';
      $('rec-doc-meta').textContent = metaLine();
      const html = (note && note.summaryHtml) || (summary ? docHtml(summary) : '');
      const cur = $('rec-sections');
      if (cur.innerHTML !== html && document.activeElement !== cur) cur.innerHTML = html;
      if (!html && st === READY) cur.innerHTML = '<div class="rc-empty">No notes were produced for this session.</div>';
      renderSegs();
      $('rec-gate').hidden = !gateText;
      $('rec-gate-text').textContent = gateText;
      /* Two different things wear this banner. In READY it is a caution — the
         notes exist and may be thin. In the error states it is a refusal.
         Same words would flatten that difference, so the colour carries it. */
      const soft = (st === READY);
      $('rec-gate').classList.toggle('rc-gate-soft', soft);
      // The numbers live here rather than on screen: available to anyone who
      // goes looking, invisible to a CHW who just wants their notes.
      if (note) {
        const bits = [];
        if (note.transcriptReach != null) bits.push('Transcript covered ' + note.transcriptReach
          + '% of the recording; largest gap ' + (note.transcriptGapSec || 0) + 's.');
        if (note.summaryGapShare != null) bits.push('Largest stretch of transcript no bullet cites: '
          + note.summaryGapShare + '% of the words.');
        $('rec-gate').title = bits.join(' ');
      }
      $('rec-retry').textContent = soft ? 'Rewrite notes' : (st === SUM_ERR ? 'Try again' : 'Retry transcription');
      // SUM_ERR needs a transcript, not audio — a session whose recording has
      // already been deleted can still have its notes written again.
      $('rec-retry').hidden = !gateText
        || (st === SUM_ERR ? !segs.length : (!soft && !hasAudio()));
      $('rec-listen').hidden = !hasAudio();
      if (!hasAudio()) $('rec-player').hidden = true;
      return;
    }

    // ---- the listening stage
    const clock = $('rec-time');
    clock.textContent = fmt(elapsed());
    clock.classList.toggle('is-idle', st === IDLE);

    $('rec-state').textContent = WORDS[st] || '';
    $('rec-who').textContent = (note && metaClient()) || '';
    $('rec-bar-title').textContent = st === RECORDING || st === PAUSED ? ((note && note.title) || '') : '';

    const busy = !!BUSY[st];
    $('rec-controls').hidden = busy;
    $('rec-start').hidden = st !== IDLE;
    $('rec-pause').hidden = !(st === RECORDING || st === PAUSED);
    $('rec-stop').hidden = !(st === RECORDING || st === PAUSED);
    $('rec-pause-label').textContent = st === PAUSED ? 'Resume' : 'Pause';
    // The filled button is always whichever action is obviously next.
    $('rec-pause').classList.toggle('rc-btn-go', st === PAUSED);

    $('rec-pick').hidden = st !== IDLE;
    $('rec-prog').hidden = !busy;
    if (!busy) $('rec-prog-fill').style.width = '0%';
    if (st === FINISHING) progress(1, 'Finishing up');

    const live = $('rec-live');
    live.hidden = !(st === RECORDING || st === PAUSED) || !liveText;
    live.style.opacity = st === PAUSED ? '.5' : '1';
  }

  function metaClient() {
    // Before Start there is no document, so the choice lives in memory.
    if (!note) return (pendingClient && pendingClient.name) || '';
    const cls = (typeof notesGetClients === 'function') ? notesGetClients(note) : [];
    return (cls.length && cls[0] && cls[0].name) ? cls[0].name : '';
  }

  /* The picker is offered, not required. Attaching a session to a client also
     shares it with whoever covers them, so forcing it would make every
     recording a sharing decision taken under time pressure. */
  function fillClients() {
    const sel = $('rec-client');
    let list = [];
    try {
      list = notesTeamMode()
        ? (teamClientsCache || [])
        : (typeof loadClients === 'function' ? loadClients() : []);
    } catch (e) { list = []; }
    const current = metaClient();
    sel.innerHTML = '<option value="">No client attached</option>' +
      list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .map(c => '<option value="' + esc(c.id) + '"' + (c.name === current ? ' selected' : '') + '>' + esc(c.name || 'Unnamed') + '</option>').join('');
  }

  /* =======================================================================
     OPEN / CLOSE
     ======================================================================= */
  /* Which route to go back to. The recorder is a full-screen overlay reachable
     from two places, so "back" cannot be a fixed destination — sending someone
     from Conversations into the Documents grid would be its own small version
     of the state bug this replaced. */
  let recOrigin = 'documents';

  /* THE READY SCREEN, WITH NO DOCUMENT BEHIND IT.

     Opening the recorder used to create a session document immediately, which
     meant every accidental click on Conversations left a blank card in the
     library. Nothing is written now until Start is pressed — before that,
     `note` is null and the chosen client is held in memory. */
  window.recOpenBlank = function recOpenBlank() {
    if (window.recActive || BUSY[st]) { recReopen(); return; }
    recOrigin = 'conversations';
    document.body.classList.add('rec-full');
    wrap.style.display = 'flex';

    note = null; segs = []; summary = null; liveText = ''; livePrimed = false; gateText = '';
    bankedMs = 0; startedMs = 0;
    // Set by the Record button on a client profile, so the Ready screen opens
    // with them already attached.
    pendingClient = window.recPendingClient || null;
    window.recPendingClient = null;
    warn('');
    $('rec-saved').textContent = '';
    $('rec-live').textContent = '';
    st = IDLE;

    fillClients();
    readColors();
    showPane('notes');
    if (!waveRaf) waveLoop();
    render();
  };

  /* Bring an in-progress session back to the front. Navigating away mid-visit
     and clicking Conversations again must return to the live recording, not
     replace it with a fresh Ready screen. */
  function recReopen() {
    document.body.classList.add('rec-full');
    wrap.style.display = 'flex';
    if (!waveRaf) waveLoop();
    render();
  }
  window.recReopen = recReopen;

  window.recOpen = function recOpen(n, origin) {
    recOrigin = origin || window.recNextOrigin || 'documents';
    window.recNextOrigin = '';
    if (window.recActive && note && n && n.id !== note.id) return;
    document.body.classList.add('rec-full');
    wrap.style.display = 'flex';
    if (note && n && note.id === n.id) return;
    pendingClient = null;

    note = n; segs = []; summary = null; liveText = ''; livePrimed = false; gateText = '';
    bankedMs = (n.durationSec || 0) * 1000; startedMs = 0;
    warn('');
    $('rec-saved').textContent = '';
    try { summary = n.summaryJson ? JSON.parse(n.summaryJson) : null; } catch (e) { summary = null; }

    /* WHICH SCREEN A SAVED CONVERSATION OPENS ON.

       This used to fall through to IDLE — the Ready recorder, with a Start
       button — whenever a session had no summaryHtml. So a conversation whose
       transcript succeeded but whose summary failed opened as if it were a
       brand new recording, offering to start one on top of itself. Start then
       refused, because hasAudio() guards it, so the button did nothing at all.

       A finished or partly-finished conversation is never the Ready screen.
       IDLE is reachable only from recOpenBlank(), where there is no document. */
    if (n.summaryHtml || n.summaryJson) {
      st = READY;                          // finished: show the document
    } else if (n.transcriptState === 'short') {
      st = TR_ERR; gateText = SHORT_MSG;   // transcript incomplete: refused, offer retry
    } else if (n.transcriptState === 'ready') {
      st = SUM_ERR;                        // transcript good, notes never written
      gateText = 'The notes for this conversation were never finished. The transcript is saved — press Try again to write them.';
    } else if ((n.durationSec || 0) > 0) {
      st = TR_ERR;                         // audio exists, transcription never completed
      gateText = n.audioOnDevice
        ? 'This conversation was interrupted before it was transcribed. The recording is still on this device — press Retry transcription.'
        : 'This conversation was interrupted before it was transcribed, and the recording is not on this device.';
    } else {
      st = TR_ERR;                         // nothing usable; recClose() will discard it
      gateText = 'This conversation was never completed and has nothing saved in it.';
    }

    fillClients();
    readColors();
    showPane('notes');
    if (!waveRaf) waveLoop();
    render();

    segLoad(n.id).then(list => {
      if (!note || note.id !== n.id) return;
      segs = list;
      if (st === READY || st === TR_ERR || st === SUM_ERR) render();
    });
  };

  /* Did this session ever amount to anything? A document should exist because
     a conversation happened, not because a button was pressed. Audio that ran
     for a moment, or a recording with no speech in it, leaves nothing behind. */
  const MIN_USEFUL_SEC = 3;
  function sessionIsUsable(n) {
    if (!n) return false;
    if (n.summaryHtml) return true;
    if (n.transcriptState === 'ready' || n.transcriptState === 'short') return true;
    return (n.durationSec || 0) >= MIN_USEFUL_SEC;
  }

  async function discardSession(n) {
    if (!n) return;
    try { await dropSession(n.id); } catch (e) {}
    try {
      if (notesTeamMode()) {
        await fbDb.collection('teams').doc(activeTeamId).collection('notes').doc(n.id).delete();
        teamNotesCache = teamNotesCache.filter(x => x.id !== n.id);
      } else {
        const data = notesLoadPersonal();
        data.notes = data.notes.filter(x => x.id !== n.id);
        notesSavePersonal(data);
      }
    } catch (e) { console.warn('could not discard empty session:', e); }
    if (notesActiveId === n.id) notesActiveId = null;
  }

  window.recClose = function recClose() {
    if (window.recActive || BUSY[st]) return;   // Stop is the only way out of a live session
    /* An abandoned session takes itself with it. Otherwise every mistimed
       Start leaves a blank card that somebody has to work out how to delete. */
    if (note && !sessionIsUsable(note)) {
      const dead = note;
      note = null;
      discardSession(dead);
    }
    /* The audio has done its job once a transcript exists, so it goes when you
       leave. Keeping it until now rather than deleting it the moment the text
       appeared is the point: it is the only window in which someone can read
       something that looks wrong and run it again. */
    if (note && note.transcriptState === 'ready' && note.audioOnDevice) {
      const id = note.id;
      dropSession(id).then(() => {
        if (typeof notesPatchActive === 'function' && notesActiveId === id) {
          notesPatchActive({ audioOnDevice: false }, true);
        }
      }).catch(e => console.warn('post-transcription audio cleanup:', e));
      note.audioOnDevice = false;
    }
    flushSave();
    if (waveRaf) { cancelAnimationFrame(waveRaf); waveRaf = 0; }
    document.body.classList.remove('rec-full');
    wrap.style.display = 'none';
    const a = $('rec-audio');
    if (a.src) { try { URL.revokeObjectURL(a.src); } catch (e) {} a.removeAttribute('src'); }
    $('rec-player').hidden = true;
    note = null; segs = []; summary = null; st = IDLE;
  };

  /* =======================================================================
     WIRING
     ======================================================================= */
  $('rec-start').addEventListener('click', start);
  $('rec-pause').addEventListener('click', pauseResume);
  $('rec-stop').addEventListener('click', stop);
  $('rec-retry').addEventListener('click', () => {
    gateText = '';
    // From READY this is "the notes look thin, write them again" — one more
    // call, and deliberately the user's decision rather than automatic.
    if (st === SUM_ERR || st === READY) summarize(); else transcribe();
  });

  [...$('rec-tabs').querySelectorAll('.rc-tab[data-pane]')].forEach(b =>
    b.addEventListener('click', () => showPane(b.dataset.pane)));

  $('rec-listen').addEventListener('click', async () => {
    const box = $('rec-player'), a = $('rec-audio');
    if (!box.hidden) { box.hidden = true; return; }
    if (!note) return;
    const parts = await readChunks(note.id).catch(() => []);
    if (!parts.length) { box.hidden = true; return; }
    if (a.src) { try { URL.revokeObjectURL(a.src); } catch (e) {} }
    const blob = new Blob(parts.map(c => c.blob), { type: note.audioMime || parts[0].type || 'audio/webm' });
    a.src = URL.createObjectURL(blob);
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  $('rec-delete').addEventListener('click', async () => {
    if (!note) return;
    if (!confirm('Delete the recording for this session? Your notes stay. This cannot be undone.')) return;
    await dropSession(note.id).catch(() => {});
    patch({ audioOnDevice: false, durationSec: note.durationSec || 0 });
    const a = $('rec-audio');
    if (a.src) { try { URL.revokeObjectURL(a.src); } catch (e) {} a.removeAttribute('src'); }
    $('rec-player').hidden = true;
    render();
  });

  $('rec-back').addEventListener('click', async () => {
    if (window.recActive) {
      if (!confirm('Still recording. Stop the recording and go back?')) return;
      await stop();
      return;   // stop() runs straight into transcription; leaving now would abandon it
    }
    if (BUSY[st]) return;
    recClose();
    /* Back always lands in the library, from either route. Conversations is
       where you create one; Documents is where every finished one lives, so
       that is the useful place to be once you are done. */
    notesActiveId = null;
    if (typeof notesOpenView === 'function') notesOpenView();
    else if (typeof notesCloseEditor === 'function') notesCloseEditor();
  });

  $('rec-doc-title').addEventListener('input', queueSave);
  $('rec-doc-title').addEventListener('blur', flushSave);
  $('rec-sections').addEventListener('input', queueSave);
  $('rec-sections').addEventListener('blur', flushSave);

  $('rec-client').addEventListener('change', () => {
    const sel = $('rec-client');
    const id = sel.value;
    const name = id ? (sel.options[sel.selectedIndex] || {}).text || '' : '';
    // No document yet: remember the choice and apply it at creation. Choosing
    // a client must not be the thing that writes a record.
    if (!note) { pendingClient = id ? { id, name } : null; render(); return; }
    const attached = id ? [{ id, name }] : [];
    patch({ clientsJson: JSON.stringify(attached), folder: name || note.folder || '' });
    render();
  });

  /* A recording lost to a closed tab is the worst failure this feature has,
     so it gets a browser prompt as well as the in-app guard. */
  window.addEventListener('beforeunload', e => {
    if (!window.recActive && !BUSY[st]) { if (note) flushSave(); return; }
    e.preventDefault(); e.returnValue = ''; return '';
  });

}

onReady(initRecorder);
